import { FC, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Drawer, Wrapper, Text } from '@lounge-fe/ui-kit'
import { t } from 'i18next'
import { FilterForm, FiltersDrawerProps } from './types'
import { GridSizeSelector } from '@/pages/Collection/Filters/GridSizeSelector'
import { TagFilterBar } from './TagFilterBar/TagFilterBar'
import { Form } from './Form'
import { useSearch } from '@/features/header/components/Header/useSearch'
import { twMerge } from 'tailwind-merge'

export const FiltersDrawer: FC<FiltersDrawerProps> = ({
  className,
  defaultValues,
  filterSections,
  isLoading,
  onSubmit,
  showGridSizeSelector,
  sortOptions,
  tags,
  ...props
}) => {
  const methods = useForm<FilterForm>({
    ...props,
    defaultValues,
  })

  useEffect(() => {
    const subscription = methods.watch(() => methods.handleSubmit(onSubmit)())
    return () => subscription.unsubscribe()
  }, [methods.handleSubmit, methods.watch])

  const { isSearchOpen } = useSearch()

  useEffect(() => {
    if (!isSearchOpen) {
      // Due to the fact the header is always rendered for SEO purposes
      // we need to reset the filters drawer form manually when search is closed
      methods.reset()
    }
  }, [isSearchOpen])

  return (
    <FormProvider {...methods}>
      {tags && tags.length > 0 && (
        <TagFilterBar name="tags" tags={tags} isLoading={isLoading} />
      )}
      <Wrapper
        size="fluid"
        className={twMerge(
          'flex flex-row justify-between py-6 border-t border-[#E7E7E7] sticky top-0 bg-primary z-[15]',
          className,
        )}
      >
        <Drawer>
          {({ onClose }) => (
            <>
              <Drawer.Trigger>
                <button className="typography--body-md underline underline-offset-2 w-fit">
                  {t('action.filter_and_sort')}
                </button>
              </Drawer.Trigger>
              <Drawer.Content direction="left" className="max-w-[450px]">
                <Drawer.Header className="sm:px-10 sm:py-8">
                  <Text variant="title-five">
                    {t('action.filter_and_sort')}
                  </Text>
                </Drawer.Header>
                <Drawer.Body className="py-4 sm:px-10">
                  <Form
                    name={'filters'}
                    sections={filterSections}
                    showActions={true}
                    onClose={onClose}
                    sortOptions={sortOptions}
                  />
                </Drawer.Body>
              </Drawer.Content>
            </>
          )}
        </Drawer>
        {showGridSizeSelector ? <GridSizeSelector /> : undefined}
      </Wrapper>
    </FormProvider>
  )
}
